import { Auth } from '@aws-amplify/auth';
import { useContext, useEffect, useState } from 'react';
import { Button, Card, Form, Spinner } from 'react-bootstrap';
import VerificationInput from 'react-verification-input';

import { BrandContext } from '../context/BrandProvider';
import ErrorLabel from '../ui/ErrorLabel';
import LoginLayout from '../ui/Layout/LoginLayout';
import LoginCard from '../ui/LoginCard';
import { buildRedirectUrl, getCurrentHostPrefix, hasRedirected } from '../utils/domainRedirect';
import ensureError from '../utils/ensureError';

type ChangeAuthState = (state: string, data?: unknown) => void;

interface LoopVerifyContactProps {
  authState?: string;
  authData?: unknown;
  changeState?: ChangeAuthState;
  onStateChange?: ChangeAuthState;
}

/**
 * Verify contact screen. Talks to @aws-amplify/auth directly (no longer extends aws-amplify-react's VerifyContact).
 */
const LoopVerifyContact: React.FC<LoopVerifyContactProps> = ({ authState, authData, changeState, onStateChange }) => {
  const brandCtx = useContext(BrandContext);
  const [loaded, setLoaded] = useState(false);
  const [code, setCode] = useState('');
  const [resent, setResent] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  const goToState: ChangeAuthState = (state, data) => {
    if (typeof onStateChange === 'function') {
      onStateChange(state, data);
    } else if (typeof changeState === 'function') {
      changeState(state, data);
    }
  };

  // AuthController always mounts this screen, so gate on authState to avoid verifying (and
  // hitting Cognito) while hidden, and reset local state each time the flow is (re-)entered.
  useEffect(() => {
    if (authState !== 'verifyContact') return undefined;

    let isMounted = true;
    setLoaded(false);
    setCode('');
    setResent(false);
    setErrorMessage(undefined);

    Auth.verifyCurrentUserAttribute('email')
      .then(() => {
        if (isMounted) setLoaded(true);
      })
      .catch((err) => {
        if (isMounted) setErrorMessage(ensureError(err).message);
      });

    return () => {
      isMounted = false;
    };
  }, [authState]);

  const submit = async (code: string): Promise<void> => {
    try {
      await Auth.verifyCurrentUserAttributeSubmit('email', code);

      const expectedPrefix = brandCtx?.domainPrefix;
      const currentPrefix = getCurrentHostPrefix();

      if (expectedPrefix && !brandCtx?.brandResolving) {
        // If we've already been redirected (query param present), do not redirect again
        if (!hasRedirected() && expectedPrefix !== currentPrefix) {
          try {
            await Auth.signOut();
          } catch {
            // best-effort sign out before the cross-domain redirect
          }
          window.location.href = buildRedirectUrl(expectedPrefix, '/login', { redirected: 'verifyContact' });
          return;
        }
      }

      goToState('signedIn', authData);
    } catch (err) {
      setErrorMessage(ensureError(err).message);
    }
  };

  const resend = (): void => {
    Auth.verifyCurrentUserAttribute('email')
      .then(() => {
        setErrorMessage(undefined);
        setResent(true);
        setLoaded(true);
      })
      .catch((err) => setErrorMessage(ensureError(err).message));
  };

  if (authState !== 'verifyContact') return null;

  return (
    <LoginLayout>
      <LoginCard>
        <Card.Title as="h1">Verify your contact details</Card.Title>
        <Card.Text>
          Your contact details changed recently. In order to allow account recovery later, we need to verify your
          contact information now.
        </Card.Text>
        {errorMessage && <ErrorLabel label={errorMessage} />}
        {loaded ? (
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              void submit(code);
              return false;
            }}
          >
            <div>
              <VerificationInput
                value={code}
                validChars="0-9"
                placeholder=""
                onChange={(value) => {
                  setCode(value);
                  if (value.length === 6) {
                    void submit(value);
                  }
                }}
                inputProps={{
                  name: 'code',
                  'aria-label': 'Six digit verification code',
                }}
                classNames={{
                  container: 'verification-input-container',
                  character: 'verification-input-character',
                  characterInactive: 'verification-input-character--inactive',
                  characterSelected: 'verification-input-character--selected',
                }}
              />
            </div>
            <Button type="submit">Submit</Button>
          </Form>
        ) : (
          !errorMessage && (
            <div className="text-center">
              <Spinner animation="border" role="status">
                <span className="sr-only">Loading...</span>
              </Spinner>
            </div>
          )
        )}
        <Form.Group className="mb-0 mt-4">
          <p className="text-center mb-0">
            {resent ? (
              'Email resent'
            ) : (
              <>
                Email didn&apos;t arrive?{' '}
                <Button variant="link" onClick={resend}>
                  Resend email
                </Button>
              </>
            )}
          </p>
        </Form.Group>
      </LoginCard>
    </LoginLayout>
  );
};

export default LoopVerifyContact;
